import { useQuery } from '@tanstack/react-query';

import { termsApi } from '../api/terms.adapter';

// Query keys are nested by typeId so a single `type(typeId)` invalidation
// flushes both the version list AND every version detail under that type — the
// publish mutation needs this because it atomically transitions two versions
// (DRAFT → PUBLISHED + previous PUBLISHED → ARCHIVED).
//
// `versionList(typeId)` and `versionDetail(typeId, versionId)` use distinct
// slot strings ('versionList' vs 'versionDetail') so prefix-matching does NOT
// make a list-only invalidation accidentally flush all detail queries.
//
// v9 (DEC-DB-PK-BIGINT-001): ids are number on the wire.
export const termsQueryKeys = {
  all: ['terms'] as const,
  types: () => [...termsQueryKeys.all, 'types'] as const,
  // Scope key — used only as a prefix for invalidation, never assigned as a queryKey.
  type: (typeId: number | string) => [...termsQueryKeys.all, 'type', typeId] as const,
  versionList: (typeId: number | string) =>
    [...termsQueryKeys.type(typeId), 'versionList'] as const,
  versionDetail: (typeId: number | string, versionId: number | string) =>
    [...termsQueryKeys.type(typeId), 'versionDetail', versionId] as const,
};

export function useTermTypesQuery() {
  return useQuery({
    queryKey: termsQueryKeys.types(),
    queryFn: () => termsApi.listTypes(),
  });
}

// Fetches versions for `typeId`. Pass `null` while no type is active — the
// query stays disabled so we don't hit the adapter with a sentinel value.
export function useTermVersionsQuery(typeId: number | null) {
  return useQuery({
    queryKey: termsQueryKeys.versionList(typeId ?? -1),
    queryFn: () => termsApi.listVersions(typeId as number),
    enabled: typeId !== null,
  });
}

// Fetches a single version (including the HTML body). Either id null → disabled.
export function useTermVersionQuery(typeId: number | null, versionId: number | null) {
  return useQuery({
    queryKey: termsQueryKeys.versionDetail(typeId ?? -1, versionId ?? -1),
    queryFn: () => termsApi.getVersion(typeId as number, versionId as number),
    enabled: typeId !== null && versionId !== null,
  });
}
